import { Box, CircularProgress, Divider, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getCompany } from "../../data/request";
import { FinancialsPanel } from "./FinancialsPanel";
import { ShareholdersPanel } from "./ShareholdersPanel";
import { CompanyDetails } from "../../types/CompanyDetails";
import { BasicInfo } from "./BasicInfo";
import { PSCPage } from "./PSCPage";
import { ChargesTable } from "./ChargesTable";
import { GroupStructurePanel } from "./GroupStructurePanel";
import { LandTable } from "./LandTable";
import { GazetteTimeline } from "./GazetteTimeline";
import { IcoRegistrationPanel } from "./IcoRegistrationPanel";
import SearchBox from "../Components/SearchBox";
import { BORDER_GREY, TEXT_SECONDARY } from "../colors";
import { OfficersPanel } from "./OfficersPanel";
import FillingHistoryTable from "./FilingHistoryTable";
import { Helmet } from "react-helmet";
import { CompanyStatus } from "../Components/CompanyStatus";
import { SectionNav, NavItem } from "../Components/SectionNav";
import { ProGate } from "../Components/ProGate";
import { useAuth } from "../../auth/AuthContext";
import { CompanyActions } from "./CompanyActions";

const NAV_ITEMS: NavItem[] = [
  { value: "overview", label: "Overview" },
  { value: "officers", label: "Officers" },
  { value: "filingHistory", label: "Filing History" },
  { value: "psc", label: "Persons with Significant Control" },
  { value: "charges", label: "Charges" },
  { value: "financials", label: "Financials", pro: true },
  { value: "shareholders", label: "Shareholders", pro: true },
  { value: "ownership", label: "Ownership Structure", pro: true },
  { value: "land", label: "Land & Properties", pro: true },
  { value: "gazetteNotices", label: "Gazette Notices", pro: true },
  { value: "icoRegistration", label: "Data Protection Registration", pro: true },
];

export const CompanyDetailsPage = () => {
  const params = useParams();
  const auth = useAuth();

  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("overview");
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails>();
  // Full-width slot below the header divider that CompanyActions portals its
  // portfolio-membership pills + Add to Portfolio button into, so that
  // content can span the whole Paper instead of being confined to the
  // narrow right-hand column the star/Export PDF row lives in.
  const [portfoliosSlot, setPortfoliosSlot] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (params.companyNo != undefined) {
        setLoading(true);
        const doc = await getCompany(params.companyNo);
        setCompanyDetails(doc);
        setLoading(false);
      }
    };

    fetchData();
  }, [params.companyNo]);

  return (
    <Box>
      <Helmet>
        <meta
          name="description"
          content={
            companyDetails?.company_name +
            " " +
            companyDetails?.registered_office_address?.postal_code
          }
        />
        <title>
          {"Directory - " +
            companyDetails?.company_name +
            " - " +
            companyDetails?.company_number}
        </title>
        {companyDetails && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              legalName: companyDetails.company_name,
              identifier: companyDetails.company_number,
              foundingDate: companyDetails.date_of_creation,
            })}
          </script>
        )}
      </Helmet>

      <Box style={{ marginBottom: 24 }}>
        <SearchBox />
      </Box>

      {loading ? (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      ) : (
        companyDetails && (
          <Box>
            <Paper style={{ padding: 24, marginBottom: 16 }} elevation={0}>
              <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                <Box>
                  <Box style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <Typography variant="h5">{companyDetails.company_name}</Typography>
                    <CompanyStatus status={companyDetails.company_status} />
                  </Box>
                  <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginTop: 4 }}>
                    Company Number: {companyDetails.company_number}
                  </Typography>
                </Box>
                {params.companyNo && (
                  <CompanyActions
                    companyNo={params.companyNo}
                    companyName={companyDetails.company_name}
                    portfoliosSlot={portfoliosSlot}
                  />
                )}
              </Box>

              <Divider sx={{ borderColor: BORDER_GREY, marginTop: 2, marginBottom: 2 }} />

              <Box ref={setPortfoliosSlot} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }} />
            </Paper>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                alignItems: { xs: "stretch", sm: "flex-start" },
              }}
            >
              <SectionNav items={NAV_ITEMS} value={section} onChange={setSection} />

              <Paper elevation={0} style={{ flex: 1, minHeight: 200, minWidth: 0 }}>
                {section === "officers" && <OfficersPanel companyNo={params.companyNo} />}
                {section === "overview" && <BasicInfo companyDetails={companyDetails} />}
                {section === "filingHistory" && (
                  <FillingHistoryTable
                    link={companyDetails.links.filing_history}
                  />
                )}
                {section === "psc" &&
                  (companyDetails.links.persons_with_significant_control ? (
                    <PSCPage
                      link={
                        companyDetails.links.persons_with_significant_control
                      }
                    />
                  ) : (
                    <Box style={{ padding: 20 }}>
                      <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                        No persons with significant control recorded for this company.
                      </Typography>
                    </Box>
                  ))}
                {section === "charges" &&
                  (companyDetails.links.charges ? (
                    <ChargesTable link={companyDetails.links.charges} />
                  ) : (
                    <Box style={{ padding: 20 }}>
                      <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                        No charges (mortgages/debentures) recorded for this company.
                      </Typography>
                    </Box>
                  ))}
                {section === "financials" && (
                  auth.isPro ? <FinancialsPanel companyNo={params.companyNo} /> : <ProGate feature="Financials" />
                )}
                {section === "shareholders" && (
                  auth.isPro ? <ShareholdersPanel companyNo={params.companyNo} /> : <ProGate feature="Shareholders" />
                )}
                {section === "ownership" && (
                  auth.isPro ? (
                    <GroupStructurePanel companyNo={params.companyNo} companyName={companyDetails.company_name} />
                  ) : (
                    <ProGate feature="Ownership Structure" />
                  )
                )}
                {section === "land" && (
                  auth.isPro ? <LandTable companyNo={params.companyNo} /> : <ProGate feature="Land & Properties" />
                )}
                {section === "gazetteNotices" && (
                  auth.isPro ? <GazetteTimeline companyNo={params.companyNo} /> : <ProGate feature="Gazette Notices" />
                )}
                {section === "icoRegistration" && (
                  auth.isPro ? (
                    <IcoRegistrationPanel companyNo={params.companyNo} />
                  ) : (
                    <ProGate feature="Data Protection Registration" />
                  )
                )}
              </Paper>
            </Box>
          </Box>
        )
      )}
    </Box>
  );
};
